import { cloneElement, isValidElement, useRef, type KeyboardEvent, type KeyboardEventHandler, type MouseEventHandler, type ReactElement, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router'
import { usePopoverPlacement } from './usePopoverPlacement'

interface DropdownProps {
  trigger: ReactNode
  children: ReactNode
  align?: 'left' | 'right'
  fitContentWidth?: boolean
  maxWidth?: number
}

/** Sostituisce data-bs-toggle="dropdown": click-outside gestito in React puro, nessuna dipendenza da bootstrap.bundle.js. */
export function Dropdown({ trigger, children, align = 'right', fitContentWidth, maxWidth }: DropdownProps) {
  const { open, setOpen, anchorRef, popRef, panelStyle, portalTarget } = usePopoverPlacement({ align, fitContentWidth, maxWidth })
  const focusLastItemOnOpen = useRef(false)

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(popRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      anchorRef.current?.querySelector<HTMLElement>('button, a, [role="button"]')?.focus()
      return
    }
    if (event.key === 'Tab') {
      requestAnimationFrame(() => setOpen(false))
      return
    }
    if (!items.length) return

    const currentIndex = items.indexOf(document.activeElement as HTMLElement)
    let nextIndex: number | undefined
    if (event.key === 'ArrowDown') nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length
    if (event.key === 'ArrowUp') nextIndex = currentIndex < 0 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = items.length - 1
    if (nextIndex !== undefined) {
      event.preventDefault()
      items[nextIndex]?.focus()
    }
  }

  const triggerElement = isValidElement<{
    onClick?: MouseEventHandler<HTMLElement>
    onKeyDown?: KeyboardEventHandler<HTMLElement>
    [key: string]: unknown
  }>(trigger)
    ? cloneElement(trigger as ReactElement<{
        onClick?: MouseEventHandler<HTMLElement>
        onKeyDown?: KeyboardEventHandler<HTMLElement>
        [key: string]: unknown
      }>, {
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        onClick: (event) => {
          trigger.props.onClick?.(event)
          if (event.defaultPrevented) return
          const willOpen = !open
          setOpen(willOpen)
          if (willOpen) {
            focusLastItemOnOpen.current = false
            requestAnimationFrame(() => popRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus())
          }
        },
        onKeyDown: (event) => {
          trigger.props.onKeyDown?.(event)
          if (event.defaultPrevented) return
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            focusLastItemOnOpen.current = event.key === 'ArrowUp'
            setOpen(true)
            requestAnimationFrame(() => {
              const items = popRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')
              if (items?.length) items[focusLastItemOnOpen.current ? items.length - 1 : 0]?.focus()
            })
          }
        },
      })
    : <span role="button" tabIndex={0} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((value) => !value)} onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setOpen((value) => !value) }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); focusLastItemOnOpen.current = event.key === 'ArrowUp'; setOpen(true) }
      }}>{trigger}</span>

  return (
    <div className="relative inline-block" ref={anchorRef}>
      {triggerElement}
      {open && portalTarget && createPortal(
        <div
          ref={popRef}
          style={panelStyle}
          className="pointer-events-auto fixed z-[var(--z-index-tooltip)] min-w-40 rounded-md border border-border bg-canvas py-1 shadow-lg"
          role="menu"
          aria-label="Azioni"
          onKeyDown={handleMenuKeyDown}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>, portalTarget,
      )}
    </div>
  )
}

export function DropdownItem({ children, onClick, href, to, className = '', external = false }: { children: ReactNode; onClick?: () => void; href?: string; to?: string; className?: string; external?: boolean }) {
  const itemClassName = `block w-full cursor-pointer px-3 py-1.5 text-left text-sm text-body hover:bg-surface ${className}`
  if (to) {
    return (
      <Link to={to} role="menuitem" tabIndex={-1} className={itemClassName}>
        {children}
      </Link>
    )
  }
  if (href) {
    return (
      <a href={href} role="menuitem" tabIndex={-1} className={itemClassName} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}>
        {children}
      </a>
    )
  }
  return (
      <button type="button" role="menuitem" tabIndex={-1} onClick={onClick} className={itemClassName}>
      {children}
    </button>
  )
}
